import { memo, useLayoutEffect, useMemo, useRef } from 'react'
import type { ZoneProps } from '../../data/types'
import { openMetric } from '../../shell/actions'
import { timeCursor } from '../../../app/time/cursor'
import { WeatherGlyph } from '../../shell/WeatherGlyph'
import { useFrameBinding, writeText } from '../../shell/bindings'
import { fmtTemp } from '../../../shared/units'
import { dayRanges, supportedDay } from './ranges'
import { WX_MARKUP } from '../../icons/catalog'
import { wxIconId } from '../../icons/codes'
import { InfoDisclosure } from '../../components/InfoDisclosure'
import './days.css'

export const TenDays = memo(function TenDays({ data }: ZoneProps) {
  const ref = useRef<HTMLElement>(null)
  const range = useMemo(() => dayRanges(data.models?.days ?? [], data.units, data.report), [data.models, data.units, data.report])
  const binding = useMemo(() => (node: HTMLElement) => {
    const buttons = [...node.querySelectorAll<HTMLButtonElement>('[data-day-index]')]
    buttons.forEach(button => button.removeAttribute('aria-current'))
    let previous = -1
    return (frame: NonNullable<ZoneProps['data']['now']>) => {
      const next = data.models?.hours[frame.index]?.dayIndex ?? -1
      if (previous === next) return
      buttons[previous]?.removeAttribute('aria-current')
      if (next >= 0) buttons[next]?.setAttribute('aria-current', 'date')
      previous = next
    }
  }, [data.models])
  useFrameBinding(ref, data, binding)
  const latest = useRef({ data, range }); latest.current = { data, range }
  useLayoutEffect(() => {
    const node = ref.current!
    node.querySelectorAll<HTMLButtonElement>('[data-day-index]').forEach((button, i) => {
      const row = range.rows[i], day = row?.day
      const supported = !!day && supportedDay(day, data.models)
      const obs = day?.isToday ? day.day.observed : null
      const observed = obs ? ` Observed so far: low ${fmtTemp(obs.low_c, data.units.temp)}, high ${fmtTemp(obs.high_c, data.units.temp)}, through ${obs.through}.` : ''
      const label = day ? `${day.name}, forecast low ${day.low ?? 'not supplied'}, high ${day.high ?? 'not supplied'}, precipitation chance ${day.chance ?? 'not supplied'}. ${supported ? 'Inspect hourly forecast.' : 'Open daily details.'}${day.hours !== 24 ? ` ${day.hours}-hour local day.` : ''}${observed}` : data.pending ? 'Daily forecast loading' : 'Daily forecast not supplied'
      button.disabled = !day; button.title = label; button.setAttribute('aria-label', label)
      writeText(button.querySelector('[data-day-name]'), day?.name ?? '—')
      writeText(button.querySelector('[data-day-chance]'), day?.chance != null ? `${day.chance} precip` : '— precip')
      writeText(button.querySelector('.v5-day-low'), day?.low ?? '—')
      writeText(button.querySelector('[data-day-high]'), day?.high ?? '—')
      const dst = button.querySelector<HTMLElement>('.v5-day-dst')!
      dst.hidden = !day || day.hours === 24
      writeText(dst, day ? `${day.hours} h` : '')
      dst.title = day ? `Clocks change: ${day.hours}-hour local day` : ''
      const glyph = button.querySelector<SVGElement>('[data-wx-icon]')!
      const key = wxIconId(day?.condition ?? 'cloudy').replace('wx-v5-', '')
      glyph.style.visibility = day ? '' : 'hidden'
      if (glyph.dataset.wxIcon !== key) { glyph.innerHTML = WX_MARKUP[key]; glyph.dataset.wxIcon = key }
      const intervals = [day?.lowValue != null && day.highValue != null && day.lowValue <= day.highValue ? { low: day.lowValue, high: day.highValue } : null, row?.low, row?.high]
      button.querySelectorAll<HTMLElement>('.v5-time-day-track > i').forEach((rail, index) => {
        const interval = intervals[index]
        rail.hidden = !interval
        if (interval) Object.assign(rail.style, range.interval(interval.low, interval.high))
      })
    })
  }, [data, range])
  // Rows stay resident; the disclosure still receives the current report's range semantics.
  const rows = useMemo(() => <ol className="v5-day-list">{Array.from({ length: 10 }, (_, i) => <li key={i}><button className="v5-day-row" data-day-index={i} onClick={() => {
      const { data, range } = latest.current, day = range.rows[i]?.day
      if (!day) return
      if (supportedDay(day, data.models)) { if (day.jumpTo == null) timeCursor.toNow(); else timeCursor.set(day.jumpTo); timeCursor.flush() }
      else openMetric('conditions', i)
    }}>
      <span className="v5-time-day-label"><span><span data-day-name>—</span><span className="v5-day-dst" hidden /></span><small data-day-chance>— precip</small></span>
      <WeatherGlyph condition="cloudy" size={22} />
      <span className="v5-day-low">—</span>
      <span className="v5-time-day-track" aria-hidden="true"><i className="v5-time-day-range" hidden /><i className="v5-time-day-bound v5-time-low-bound" hidden /><i className="v5-time-day-bound v5-time-high-bound" hidden /></span>
      <strong data-day-high>—</strong>
    </button></li>)}</ol>, [])
  return <section ref={ref} className="v5-days v5-zone v5-time-days" data-zone="days" data-qa="time-days" id="v5-days" aria-labelledby="v5-days-title">
    <div className="v5-zone-heading"><h2 id="v5-days-title">10-day forecast</h2><span className="v5-note">10 days · °{data.units.temp}</span></div>
    {rows}
    <div className="v5-time-day-caption"><p>Daily highs & lows · local time</p><InfoDisclosure label="About the 10-day forecast"><p>Daily guidance and hourly extremes cover each local calendar day. Today’s observations are separate from the daily forecast.</p><p>{range.hasBounds ? 'Fine rails show supplied model bounds, not calibrated odds.' : 'Uncertainty bounds were not supplied.'} Select a day to inspect its hourly forecast or open daily details when hours are unavailable.</p></InfoDisclosure></div>
  </section>
})
